import { Add, FilterList } from '@mui/icons-material'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import PanelTable from './PanelTable'
import AddorEditPanelAndStakeholder from './AddorEditPanelAndStakeholder'
import { addPanelAndStakeholder, deletePanelOrStakeholder, getPanelAndStakeholders, getPanelAndStakeholdersDropdown } from './panelAndStakeholdersApi'
import { useParams } from 'react-router-dom'
import { useSnackbar } from '../../../../Context/SnackbarContext'
import { theme } from '../../../../theme'
import FilterPanelAndStakeholders from './FilterPanelAndStakeholders'
import SearchBar from '../../../Common/Search/SearchBar'

export interface panelAndStakeholdersDropdown {
  RoleNum: number,
  RoleKey: string
}

export interface PanelAndStakeholder {
  ParticipantID: number;
  UserName: string;
  EmailId: string;
  RoleKey: string;
  RoleNum: number;
  UpdatedBy: string
}

export interface formData {
  username: string,
  email: string,
  role: null | number
}

export interface PanelFilters {
  selectedFilters: string[];
  filterText: { [key: string]: string };
  role: string;
}

const PanelStakeholders: React.FC = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Set initial rows per page
  const [totalCount, setTotalCount] = useState(0);
  const [panelData, setPanelData] = useState<PanelAndStakeholder[]>([]);
  const [isAddorEditOpen, setIsAddOrEditOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [formData, setFormData] = useState<formData>({
    username: "",
    email: "",
    role: null,
  });
  const { tenant } = useParams<{ tenant: string }>();
  const [panelAndStakeholders, setPanelAndStakeholders] = useState<panelAndStakeholdersDropdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const { showSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // State for delete confirmation dialog
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [panelFilters, setPanelFilters] = useState<PanelFilters>({
    selectedFilters: [],
    filterText: {},
    role: '',
  });
  const [searchQuery, setSearchQuery] = useState('')
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);

  const hasActiveFilters = (filters: typeof panelFilters): boolean => {
    return (
      filters.role.trim() !== '' ||
      filters.selectedFilters.length > 0 ||
      Object.values(filters.filterText || {}).some(val => val?.trim() !== '')
    );
  };


  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to the first page when rows per page changes
  };

  const fetchPanelAndStakeholdersDropdown = async () => {
    const data = await getPanelAndStakeholdersDropdown(tenant);
    setPanelAndStakeholders(data);
  };

  const fetchPanelandStakeholderData = async (
    filters = panelFilters,
    pageNumber = page,
    pageSizeNumber = rowsPerPage,
    name = searchQuery
  ) => {
    try {
      setLoading(true);

      const payLoad: any = {
        page: pageNumber,
        pageSize: pageSizeNumber,
        role: filters.role,
        userName: name
      };
      if (filters.selectedFilters?.length && filters.filterText) {
        filters.selectedFilters.forEach((filter: string) => {
          const value = filters.filterText[filter]?.trim();
          if (value) {
            payLoad[filter] = value;
          }
        });
      }
      const result = await getPanelAndStakeholders(tenant, payLoad)

      if (result) {
        const { Data, TotalCount } = result
        setPanelData(Array.isArray(Data) ? Data : []); // <-- Ensure array
        setTotalCount(TotalCount)
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }

  }

  useEffect(() => {
    fetchPanelAndStakeholdersDropdown()
  }, [tenant])

  const cancelDeleteUser = () => {
    setSelectedId(0)
    setDeleteDialogOpen(false)
  }

  useEffect(() => {
    fetchPanelandStakeholderData(panelFilters, page, rowsPerPage, searchQuery)
  }, [page, rowsPerPage])

  const handleApplyFilters = (newFilters: {
    selectedFilters: string[];
    filterText: { [key: string]: string };
    role: string;
  }) => {
    const isFiltered = hasActiveFilters(newFilters);

    if (isFiltered) {
      setPanelFilters(newFilters);
      fetchPanelandStakeholderData(newFilters, 1, 10);
    }

    setOpenFilterDialog(false)
  };

  const handleClearFilterAPI = async () => {
    const clearedFilters = {
      selectedFilters: [],
      filterText: {},
      role: '',
    };
    setPanelFilters(clearedFilters);
    fetchPanelandStakeholderData(clearedFilters, 1, 10);
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // 🚫 Skip on initial render
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const updatedFilters = {
        ...panelFilters,
      };
      fetchPanelandStakeholderData(updatedFilters, page, rowsPerPage, searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const handleSave = async () => {
    const payLoad = {
      ParticipantID: selectedId,
      UserName: formData.username,
      EmailId: formData.email,
      RoleNum: formData.role,
    }
    const result = await addPanelAndStakeholder(payLoad, tenant);
    if (result) {
      showSnackbar(result.Message, result.Status ? "success" : "error");
      if (result.Status) {
        setIsAddOrEditOpen(false)
        fetchPanelandStakeholderData()
      }

    } else {
      showSnackbar("Failed to add panel member or stakeholder.", "error");
    }
  }

  const handleEditMember = (id: number) => {
    const memberToEdit = panelData.find((member) => member.ParticipantID === id)
    if (memberToEdit) {
      setMode("edit")
      setSelectedId(memberToEdit.ParticipantID)
      setFormData({
        username: memberToEdit.UserName,
        email: memberToEdit.EmailId,
        role: memberToEdit.RoleNum
      })
      setIsAddOrEditOpen(true)
    }
  }

  const handlOpenDelteDialog = (id: number) => {
    setSelectedId(id)
    setDeleteDialogOpen(true)
  }

  const handleDelete = async () => {
    if (!selectedId) {
      showSnackbar("Please the data to delete.", "error")
      return
    }
    const result = await deletePanelOrStakeholder(tenant, { "ParticipantID": selectedId })
    if (result) {
      showSnackbar("Data was deleted successfully", "success");

      // Filter out the deleted participant from the state
      setPanelData((prev) => prev.filter(item => item.ParticipantID !== selectedId));

      // Optionally reset selected ID
      setSelectedId(0);
    } else {
      showSnackbar("Failed to delete data", "error");
    }
    setDeleteDialogOpen(false)
  }

  return (
    <Box sx={{
      marginLeft: '2.3vw',
      marginRight: '2.3vw',
    }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        mb='1.6vw'
      >
        {/* Title and subtitle */}
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Manage Panel Members & Stakeholders
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Create and Manage Panel Members and Stakeholders in One Centralized Place.
          </Typography>
        </Box>

        {/* Actions: Search + Filters + Add */}
        <Box display="flex" gap={1} flexWrap="wrap">
          {/* <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder="Search by name"
          /> */}
          <Button
            size="small"
            variant="outlined"
            sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}
            onClick={() => { setOpenFilterDialog(true) }}
          >
            Filter & Sort
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setIsAddOrEditOpen(true); setMode("add"); setFormData({
                username: "",
                email: "",
                role: null,
              })
            }}
            sx={{
              height:'2.7vw'
            }}
          >
            Add Participant
          </Button>
        </Box>
      </Box>

      <FilterPanelAndStakeholders
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
        onApply={handleApplyFilters}
        filters={panelFilters}
        handleClearFilterAPI={handleClearFilterAPI}
      />
      <Box>
        <PanelTable
          totalCount={totalCount}
          panelData={panelData}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          loading={loading}
          handleEditMember={handleEditMember}
          handlOpenDelteDialog={handlOpenDelteDialog}

        />
      </Box>

      <AddorEditPanelAndStakeholder
        open={isAddorEditOpen}
        onClose={() => {setIsAddOrEditOpen(false);setSelectedId(0)}}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        panelAndStakeholders={panelAndStakeholders}
        mode={mode}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteUser}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Panel Member or Stakeholder? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteUser} sx={{ color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button onClick={handleDelete}
            variant="contained"
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: theme.palette.error.dark,
              },
            }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PanelStakeholders